'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useLocalStorageState } from '~/modules/browser/storage';
import { LocalJSONStorage } from '~/utils/js';
import { wishlistHovercardEvent } from '~/features/cart/cartHovercardEvents';
export type WishlistItem = {
  activityId: number;
  name: string;
  slug: string;
  imagePath: string | null;
  variantId: number | null;
  languageId: number | null;
  /** The language code at the time of adding (e.g. 'nl', 'fr') for auto-selecting language */
  addedLanguage?: string | null;
  /** Number of participants for price estimation */
  participants?: number | null;
  addedAt: string;
};
export function wishlistUpsertItems(prev: WishlistItem[], item: Omit<WishlistItem, 'addedAt'>, addedAtIso: string): WishlistItem[] {
  const existing = prev.find(x => x.activityId === item.activityId);
  if (!existing) {
    return [...prev, {
      ...item,
      addedAt: addedAtIso
    }];
  }
  return prev.map(x => x.activityId === item.activityId ? {
    ...x,
    ...item
  } : x);
}
export function wishlistRemoveItem(prev: WishlistItem[], activityId: number): WishlistItem[] {
  return prev.filter(item => item.activityId !== activityId);
}
export function wishlistToggleItem(prev: WishlistItem[], item: Omit<WishlistItem, 'addedAt'>, addedAtIso: string): {
  next: WishlistItem[];
  added: boolean;
} {
  const exists = prev.some(x => x.activityId === item.activityId);
  if (exists) {
    return {
      next: prev.filter(x => x.activityId !== item.activityId),
      added: false
    };
  }
  return {
    next: [...prev, {
      ...item,
      addedAt: addedAtIso
    }],
    added: true
  };
}
export function wishlistUpdateSelection(prev: WishlistItem[], input: {
  activityId: number;
  variantId?: number | null;
  languageId?: number | null;
  participants?: number | null;
}): WishlistItem[] {
  const itemIndex = prev.findIndex(item => item.activityId === input.activityId);
  if (itemIndex === -1) return prev;
  const row = prev[itemIndex]!;
  const next = [...prev];
  next[itemIndex] = {
    ...row,
    variantId: input.variantId === undefined ? row.variantId : input.variantId,
    languageId: input.languageId === undefined ? row.languageId : input.languageId,
    participants: input.participants === undefined ? row.participants : input.participants
  };
  return next;
}
const WISHLIST_STORAGE = new LocalJSONStorage<WishlistItem[]>('wishlist.activities', [], 'localStorage');
export default function useWishlistState() {
  const $ = _c(2);
  const storage = useLocalStorageState(WISHLIST_STORAGE);
  let t0;
  if ($[0] !== storage) {
    t0 = {
      ...storage,
      items: storage.state,
      isInWishlist(activityId) {
        return storage.state.some(item => item.activityId === activityId);
      },
      upsert(item_0, opts) {
        storage.setState(prev => wishlistUpsertItems(prev, item_0, new Date().toISOString()));
        if (!opts?.silent) {
          wishlistHovercardEvent.emit();
        }
      },
      remove(activityId_0) {
        storage.setState(prev_0 => wishlistRemoveItem(prev_0, activityId_0));
      },
      toggle(item_1) {
        let added = false;
        storage.setState(prev_1 => {
          const result = wishlistToggleItem(prev_1, item_1, new Date().toISOString());
          added = result.added;
          return result.next;
        });
        if (added) {
          wishlistHovercardEvent.emit();
        }
        return added;
      },
      updateSelection(input) {
        storage.setState(prev_2 => wishlistUpdateSelection(prev_2, input));
      },
      clear() {
        storage.setState([]);
      }
    };
    $[0] = storage;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}